import { useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@wa/ui";
import {
  PORTAL_MODULES,
  grantedModules,
  resolveCompanyGrant,
  type PortalModule,
  type PortalModuleGrant,
} from "@wa/core/portal-modules";
import {
  PORTAL_PROJECT_TABS,
  PORTAL_PROJECT_TAB_FLOOR,
  grantedProjectTabs,
  resolveProjectTabGrant,
  type PortalProjectTab,
  type PortalProjectTabGrant,
} from "@wa/core/portal-project-tabs";
import { useT } from "../../../i18n/I18nProvider";
import {
  setClientPortalModules, setClientPortalProjectTabs,
  type Client, type Contact,
} from "../../../lib/clients";
import { GrantPills } from "./GrantPills";
import { PortalShutOutList } from "./PortalShutOutList";

type T = ReturnType<typeof useT>;

/**
 * The MIDDLE level of the portal grant: what this one company may open.
 *
 * Three levels decide it. A CRM-wide default (the `crm.portal` Setting), this
 * company's row, and each person's row on the contacts tab. This screen owns the
 * middle one, and the two things it has to make obvious are the two an operator
 * cannot work out from a row of ticks:
 *
 *   1. what an inherited grant actually opens, because `null` here means "did
 *      not decide" and NOT "everything" - printing "all modules" over a default
 *      that closed two is the one direction a permissions screen must not err in;
 *   2. who this company's grant has emptied, which is the intersect rule's one
 *      genuinely invisible consequence (see `PortalShutOutList`).
 *
 * ── THE CLIENT LEVEL REPLACES THE DEFAULT, IT DOES NOT NARROW IT ───────────
 *
 * Which is why no pill here is ever drawn struck through. A default of
 * `["invoices"]` does not close anything for this company: ticking `support`
 * opens support for them alone, and that upward move is the whole reason the
 * per-client control exists - the alternative would be widening the default for
 * everybody in order to sell one client one extra page. The contact level below
 * is the one that can only subtract, and that is where `unavailable` belongs.
 *
 * ── SAVE IS DEFERRED HERE, PER-TICK ON THE CONTACT ─────────────────────────
 *
 * A family edit ("invoices and support, nothing else") is several clicks, and at
 * this level each intermediate state that reached the server would be a real
 * access change with its own audit row - a company briefly closed down to one
 * module on its way to two. The contact control saves on every tick for the
 * opposite reason: it is one row, one decision, and a Save button there would
 * add an unsaved state to a control whose whole job is to make the current one
 * obvious. `GrantPills` deliberately leaves that choice to each caller.
 */
export function PortalTab({ t, client, canManage, onChange }: {
  t: T;
  client: Client & { contacts: Contact[] };
  canManage: boolean;
  onChange: () => void;
}) {
  /**
   * The CRM-wide default, as the detail route sent it.
   *
   * The `??` covers an API build that predates the field. Both halves fall back
   * to `null`, which is "no rule at all" - the same thing an installation that
   * has never configured the portal has, and the state in which this screen's
   * arithmetic is trivially correct. It cannot overstate access: a default we
   * failed to receive would show as an inherited set that is too WIDE, and the
   * grant this screen writes is absolute rather than relative to it, so nothing
   * saved from here depends on the number being right.
   */
  const defaults = client.portalDefaults ?? { modules: null, projectTabs: null };

  // What the server last confirmed, beside what the operator is editing. Both
  // are needed: "dirty" is the difference, and only the halves that differ are
  // sent (see `save`).
  const [savedModules, setSavedModules] = useState<PortalModuleGrant>(client.portalModules ?? null);
  const [savedTabs, setSavedTabs] = useState<PortalProjectTabGrant>(client.portalProjectTabs ?? null);
  const [modules, setModules] = useState<PortalModuleGrant>(client.portalModules ?? null);
  const [tabs, setTabs] = useState<PortalProjectTabGrant>(client.portalProjectTabs ?? null);
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // Seeded once and deliberately not resynced when the parent reloads the
  // client: the reload happens BECAUSE of a save from this screen or from the
  // shut-out list below, and following it would throw away pills ticked while
  // the request was in flight.
  const dirty = !sameGrant(modules, savedModules) || !sameGrant(tabs, savedTabs);

  async function save() {
    setState("saving");
    try {
      // Only the half that changed. A PUT that rewrites an identical value still
      // files a `crm.client.portal_access_changed` audit row, and an audit trail
      // padded with non-changes is one nobody reads to the end. It also leaves a
      // partial failure resumable: the first call's result is committed to
      // `saved*` before the second is attempted, so a retry sends only the rest.
      if (!sameGrant(modules, savedModules)) {
        const r = await setClientPortalModules(client.id, modules);
        setModules(r.portalModules);
        setSavedModules(r.portalModules);
      }
      if (!sameGrant(tabs, savedTabs)) {
        const r = await setClientPortalProjectTabs(client.id, tabs);
        setTabs(r.portalProjectTabs);
        setSavedTabs(r.portalProjectTabs);
      }
      setState("saved");
      // The reload is what makes the shut-out list below tell the truth about
      // the grant that was just written - it reads the server's resolved answer
      // for every contact, which only this refetch can bring back.
      onChange();
      setTimeout(() => setState("idle"), 1500);
    } catch {
      setState("error");
    }
  }

  const effectiveModules = grantedModules(resolveCompanyGrant(defaults.modules, modules));
  const effectiveTabs = grantedProjectTabs(
    resolveProjectTabGrant({ global: defaults.projectTabs, client: tabs }),
  );
  const moduleLabel = (m: PortalModule) => t(`clients.portalModules.m.${m}`);
  const tabLabel = (tab: PortalProjectTab) => t(`clients.portalTabs.t.${tab}`);

  return (
    <div className="space-y-5">
      <PortalShutOutList
        t={t}
        clientId={client.id}
        contacts={client.contacts}
        // The SAVED grant, not the draft: the rows below were resolved against
        // it, and pairing a saved fact with a drafted parent would let the box
        // claim a cause that is not in force yet.
        companyGrant={resolveCompanyGrant(defaults.modules, savedModules)}
        canManage={canManage}
        onChange={onChange}
      />

      <Section title={t("clients.portal.modulesTitle")} hint={t("clients.portal.modulesHint")}>
        <GrantPills
          items={PORTAL_MODULES}
          grant={modules}
          onChange={(next) => { setModules(next); setState("idle"); }}
          labelFor={moduleLabel}
          // The default's set, so the first untick out of "inherited" subtracts
          // one module from what this company already had rather than writing
          // the whole vocabulary into their row and silently widening them.
          baseWhenNull={grantedModules(defaults.modules)}
          clearLabel={t("clients.portal.inherit")}
          disabled={!canManage || state === "saving"}
        />
        <Effective
          t={t}
          inherited={modules === null}
          names={effectiveModules.map(moduleLabel)}
          count={effectiveModules.length}
          total={PORTAL_MODULES.length}
          label={t("clients.portal.effectiveModules")}
        />
      </Section>

      <Section title={t("clients.portal.tabsTitle")} hint={t("clients.portal.tabsHint")}>
        <GrantPills
          items={PORTAL_PROJECT_TABS}
          grant={tabs}
          onChange={(next) => { setTabs(next); setState("idle"); }}
          labelFor={tabLabel}
          baseWhenNull={grantedProjectTabs(defaults.projectTabs)}
          // Drawn permanently on. A project page with no tabs is a header over
          // nothing; the way to hide the page is to close the `projects` MODULE
          // above, which is a different control and the hint says so.
          pinned={PORTAL_PROJECT_TAB_FLOOR}
          clearLabel={t("clients.portal.inherit")}
          disabled={!canManage || state === "saving"}
        />
        <Effective
          t={t}
          inherited={tabs === null}
          names={effectiveTabs.map(tabLabel)}
          count={effectiveTabs.length}
          total={PORTAL_PROJECT_TABS.length}
          label={t("clients.portal.effectiveTabs")}
        />
      </Section>

      {canManage ? (
        <div className="flex items-center justify-end gap-2">
          {state === "error" && (
            <span className="text-xs text-red-600 dark:text-red-400">{t("clients.portal.saveFailed")}</span>
          )}
          <Button size="sm" onClick={() => void save()} disabled={state === "saving" || !dirty}>
            {state === "saving" ? <Loader2 className="size-3.5 animate-spin" /> : null}
            {state === "saved" ? t("common.saved") : t("common.save")}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t("clients.portal.readonly")}</p>
      )}
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="mb-2 mt-0.5 text-xs text-muted-foreground">{hint}</p>
      {children}
    </div>
  );
}

/**
 * What a contact with no override of their own would actually get.
 *
 * Spelled out rather than counted, because the count alone answers the wrong
 * question: "3 of 6" tells an operator that something is closed and not which
 * thing. The count is drawn beside the names all the same - it is the part that
 * survives a glance.
 *
 * The digits sit OUTSIDE the translated string on purpose. A message printing
 * `{n}` has to be a `{ one, other }` entry in both catalogues (the CRM's plural
 * rule, pinned by `i18n/catalogues.test.ts`), and a bare "3/6" governs no noun
 * in either language, so a plural entry here would be two identical branches
 * maintained forever.
 */
function Effective({ t, inherited, names, count, total, label }: {
  t: T;
  inherited: boolean;
  names: string[];
  count: number;
  total: number;
  label: string;
}) {
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      <span className="font-medium">{label}</span>{" "}
      <span className="tabular-nums">{count}/{total}</span>
      {names.length > 0 ? <> - {names.join(", ")}</> : <> - {t("clients.portal.effectiveNone")}</>}
      {inherited && <> ({t("clients.portal.inheritedNote")})</>}
    </p>
  );
}

/**
 * Are these two grants the same decision?
 *
 * `null` is a value here, not a missing one, so it compares only with itself: a
 * client that decided nothing and a client that decided on the same list the
 * default happens to hold are different rows with different futures, and saving
 * one over the other is a real change.
 *
 * Order-sensitive, which is safe rather than lucky: every array on either side
 * of this comparison came out of `PORTAL_MODULES.filter(...)` or
 * `PORTAL_PROJECT_TABS.filter(...)` - `GrantPills` on the way in, the vocabulary
 * parsers on the way back - so both are always in vocabulary order.
 */
function sameGrant<G extends string>(a: G[] | null, b: G[] | null): boolean {
  if (a === null || b === null) return a === b;
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
